import { useMemo } from "react";

import { QueryKey, useQuery } from "@tanstack/react-query";

import { FilterableInventory, useInventoryListFeatureFilter } from "./use-inventory-list-feature-filter";

const INVENTORY_QUERY_STALE_TIME = 2 * 60 * 1000;

interface UseInventoriesQueryParams<TInventory extends FilterableInventory> {
  workspaceId: string;
  queryKey: QueryKey;
  queryFn: () => Promise<readonly (TInventory | null)[]>;
  enabled: boolean;
  retry?: boolean;
}

export const useInventoriesQuery = <TInventory extends FilterableInventory>({
  workspaceId,
  queryKey,
  queryFn,
  enabled,
  retry,
}: UseInventoriesQueryParams<TInventory>) => {
  const filterVisibleInventories = useInventoryListFeatureFilter(workspaceId);

  const queryResult = useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime: INVENTORY_QUERY_STALE_TIME,
    retry,
  });

  const inventoryList = useMemo(
    () => filterVisibleInventories(queryResult.data ?? []),
    [queryResult.data, filterVisibleInventories],
  );

  return {
    inventoryList,
    loading: queryResult.isLoading,
    error: queryResult.error,
    queryResult,
  };
};
